// Admin > Diagnostics > "Derived data" (diagnosticsUI.ts; platform_admin):
// precomputed artefacts that are missing or older than their inputs —
// HEC-RAS catchment projects, flood baselines, serving bundles, package
// runs, strategy outputs, complexity variants, reference-layer caches
// (GET /api/admin/diagnostics/derived, wcc_viewer/derived_data.py).
//
//   - a summary line and one collapsible group per area; stale / missing /
//     error rows are highlighted with the reason and the suggested fix, and
//     groups with anything to fix start open;
//   - HEC-RAS rows get "Prepare" / "Refresh": queues a background
//     `hecras_prepare` job (prepare on the server, then a terrain job on an
//     online compute node with ras-terrain), disabled with the reason when
//     no suitable node is online; progress is polled while it runs;
//   - refreshDerivedBadge(): a count badge on the Diagnostics tab button
//     (and the Admin workspace option) when anything needs attention.
import { getJson, postJson } from "./api";
import { el } from "./legend";

type Status = "ok" | "stale" | "missing" | "error";

interface JobSummary {
  id: string;
  status: string;
  progress: number | null;
  message: string | null;
  error: string | null;
  created_at?: string | null;
  finished_at?: string | null;
}

interface DerivedRow {
  id: string;
  name: string;
  status: Status;
  reason: string;
  updated_at: string | null;
  input_updated_at: string | null;
  fix: string | null;
  detail?: string | null;
  action?: { kind: string; catchment: string; label: string; enabled: boolean; disabled_reason: string | null };
  job?: JobSummary | null;
}

interface DerivedGroup {
  key: string;
  label: string;
  counts: Record<Status, number>;
  rows: DerivedRow[];
}

export interface DerivedData {
  generated_at: string;
  summary: Record<Status, number>;
  attention: number;
  groups: DerivedGroup[];
}

let hostEl: HTMLElement | null = null;
let data: DerivedData | null = null;
let loadError = "";
let loading = false;
let actionError = "";
const openGroups = new Map<string, boolean>(); // user's own open/closed choice per group
const jobLogs = new Map<string, string[]>(); // job id -> log tail
let pollTimer: number | undefined;

const ACTIVE = new Set(["queued", "running"]);

function fmtTime(s: string | null | undefined): string {
  if (!s) return "–";
  const d = new Date(s.includes("T") || s.includes("Z") ? s : s.replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString();
}

export function summaryText(sm: Record<Status, number>): string {
  const parts: string[] = [];
  if (sm.stale) parts.push(`${sm.stale} stale`);
  if (sm.missing) parts.push(`${sm.missing} missing`);
  if (sm.error) parts.push(`${sm.error} check error${sm.error === 1 ? "" : "s"}`);
  return parts.length ? `${parts.join(", ")} (${sm.ok} ok)` : `All ${sm.ok} derived artefacts are up to date`;
}

function redraw(): void {
  if (hostEl) draw(hostEl);
}

function activeJobs(): JobSummary[] {
  const out: JobSummary[] = [];
  for (const g of data?.groups ?? []) for (const r of g.rows) if (r.job && ACTIVE.has(r.job.status)) out.push(r.job);
  return out;
}

function schedulePoll(): void {
  window.clearTimeout(pollTimer);
  if (!activeJobs().length || !hostEl?.isConnected) return;
  pollTimer = window.setTimeout(() => void pollJobs(), 5000);
}

async function pollJobs(): Promise<void> {
  const running = activeJobs();
  let finished = false;
  for (const j of running) {
    try {
      const r = await getJson<{ job: JobSummary | null; log: string[] }>(
        `/api/admin/diagnostics/derived/jobs/${j.id}?lines=12`,
      );
      if (!r.job) {
        finished = true;
        continue;
      }
      Object.assign(j, r.job);
      jobLogs.set(j.id, r.log);
      if (!ACTIVE.has(r.job.status)) finished = true;
    } catch {
      /* transient; next poll retries */
    }
  }
  if (finished) await load();
  else {
    redraw();
    schedulePoll();
  }
}

export async function load(): Promise<void> {
  if (loading) return;
  loading = true;
  redraw();
  try {
    data = await getJson<DerivedData>("/api/admin/diagnostics/derived");
    loadError = "";
    setBadge(data.attention);
  } catch (err) {
    loadError = `Couldn't load the derived-data checks: ${(err as Error).message}`;
  } finally {
    loading = false;
    redraw();
    schedulePoll();
  }
}

async function runAction(row: DerivedRow): Promise<void> {
  if (!row.action) return;
  actionError = "";
  try {
    const r = await postJson<{ job: JobSummary }>("/api/admin/diagnostics/derived/hecras/prepare", {
      catchment_id: row.action.catchment,
    });
    row.job = r.job;
    row.action.enabled = false;
    row.action.disabled_reason = "a prepare job is already running";
  } catch (err) {
    actionError = `${row.name}: ${(err as Error).message}`;
  }
  redraw();
  schedulePoll();
}

function statusCell(s: Status): HTMLElement {
  return el("span", { className: `derived-status derived-status-${s}` }, s);
}

function drawJob(td: HTMLElement, j: JobSummary): void {
  const pct = j.progress != null ? ` ${Math.round(j.progress * 100)}%` : "";
  const cls = j.status === "error" || j.status === "timeout" ? "doc-viewer-error" : "caption";
  td.append(
    el("div", { className: cls }, `Prepare job ${j.status}${pct}${j.message ? ` — ${j.message}` : ""}` +
      (j.error ? ` (${j.error})` : "")),
  );
  const lines = jobLogs.get(j.id);
  if (lines?.length && ACTIVE.has(j.status)) td.append(el("pre", { className: "compute-pre" }, lines.join("\n")));
}

function drawRow(t: HTMLElement, r: DerivedRow): void {
  const tr = document.createElement("tr");
  if (r.status !== "ok") tr.className = `derived-row-${r.status}`;
  const what = el("td", {});
  what.append(el("div", {}, r.name));
  if (r.detail && r.detail !== r.status) what.append(el("div", { className: "caption" }, r.detail));
  const why = el("td", {});
  why.append(el("div", {}, r.reason || "–"));
  if (r.status !== "ok" && r.fix) why.append(el("div", { className: "derived-fix" }, `Fix: ${r.fix}`));
  if (r.action) {
    const btn = el("button", { type: "button", disabled: !r.action.enabled }, r.action.label);
    if (!r.action.enabled && r.action.disabled_reason) btn.title = r.action.disabled_reason;
    else btn.title = "Write the project on the server, then make its terrain on a HEC-RAS compute node (background job)";
    btn.addEventListener("click", () => void runAction(r));
    const box = el("div", { className: "derived-action" });
    box.append(btn);
    if (!r.action.enabled && r.action.disabled_reason) {
      box.append(el("span", { className: "caption" }, ` ${r.action.disabled_reason}`));
    }
    why.append(box);
  }
  if (r.job) drawJob(why, r.job);
  tr.append(
    el("td", {}, ""),
    what,
    why,
    el("td", { className: "caption" }, fmtTime(r.updated_at)),
    el("td", { className: "caption" }, fmtTime(r.input_updated_at)),
  );
  tr.firstElementChild!.append(statusCell(r.status));
  t.append(tr);
}

function drawGroup(root: HTMLElement, g: DerivedGroup): void {
  const bad = g.counts.stale + g.counts.missing + g.counts.error;
  const det = el("details", { className: "derived-group" }) as HTMLDetailsElement;
  det.open = openGroups.get(g.key) ?? bad > 0;
  det.addEventListener("toggle", () => openGroups.set(g.key, det.open));
  const sum = el("summary", {});
  sum.append(el("strong", {}, g.label), el("span", { className: bad ? "derived-count-bad" : "caption" },
    ` — ${g.rows.length ? summaryText(g.counts) : "nothing to check on this server"}`));
  det.append(sum);
  if (g.rows.length) {
    const t = el("table", { className: "detail-table derived-table" });
    const head = document.createElement("tr");
    for (const h of ["Status", "Artefact", "Reason / fix", "Made", "Inputs as of"]) head.append(el("th", {}, h));
    t.append(head);
    // problems first, original order otherwise
    const order: Record<Status, number> = { error: 0, missing: 1, stale: 2, ok: 3 };
    [...g.rows].sort((a, b) => order[a.status] - order[b.status]).forEach((r) => drawRow(t, r));
    const wrap = el("div", { className: "admin-table-scroll" });
    wrap.append(t);
    det.append(wrap);
  }
  root.append(det);
}

function draw(root: HTMLElement): void {
  root.innerHTML = "";
  const header = el("div", { className: "admin-pane-header" });
  header.append(el("h3", {}, "Derived data"));
  const btn = el("button", { type: "button", disabled: loading }, loading ? "Checking…" : "Re-check");
  btn.addEventListener("click", () => void load());
  header.append(btn);
  root.append(header);
  root.append(el("p", { className: "caption" },
    "Precomputed artefacts compared with what they are derived from (file times and manifests only). " +
    "Stale or missing ones are listed with the fix."));
  if (loadError) root.append(el("p", { className: "doc-viewer-error" }, loadError));
  if (actionError) root.append(el("p", { className: "doc-viewer-error" }, actionError));
  if (!data) {
    if (!loadError) root.append(el("p", { className: "caption" }, "Checking…"));
    return;
  }
  root.append(el("p", { className: data.attention ? "derived-count-bad" : "caption" },
    `${summaryText(data.summary)}. Checked ${fmtTime(data.generated_at)}.`));
  for (const g of data.groups) drawGroup(root, g);
}

/** Renders into its own host element inside the Diagnostics tab. */
export function renderDerivedDataSection(root: HTMLElement): void {
  hostEl = root;
  draw(root);
  if (data === null) void load();
  else schedulePoll();
}

function setBadge(n: number): void {
  const btn = document.getElementById("admin-diagnostics-tab-btn");
  if (btn) {
    let badge = btn.querySelector<HTMLElement>(".tab-badge");
    if (n > 0) {
      if (!badge) {
        badge = el("span", { className: "tab-badge" });
        btn.append(badge);
      }
      badge.textContent = String(n);
      badge.title = `${n} derived artefact(s) stale or missing (Derived data section)`;
    } else badge?.remove();
  }
  const opt = document.querySelector<HTMLOptionElement>('#workspace-select option[value="admin"]');
  if (opt) {
    const base = opt.dataset.baseLabel ?? opt.textContent ?? "";
    opt.dataset.baseLabel = base;
    opt.textContent = n > 0 ? `${base} (${n} to check)` : base;
  }
}

/** Fetch the checks once in the background (platform admins) just to set
 *  the badge; the section reuses the result when the tab is opened. */
export function refreshDerivedBadge(delayMs = 4000): void {
  window.setTimeout(() => {
    if (data === null && !loading) void load();
  }, delayMs);
}
